import { template as template_0f757811d496452cbaea16cf4db6c601 } from "@ember/template-compiler";
import { service } from '@ember/service';
import { all, task, timeout } from 'ember-concurrency';
import { taskTypes } from 'eflex/constants/tasks/task-types';
import { tracked } from '@glimmer/tracking';
import { waitFor } from '@ember/test-waiters';
import { sum } from 'ramda';
import { TrackedArray } from 'tracked-built-ins';
import Component from '@glimmer/component';
import Breadcrumbs from 'eflex/components/breadcrumbs';
import CheckboxInput from 'eflex/components/checkbox-input';
import { fn } from '@ember/helper';
import { inputNum } from 'eflex/helpers';
import { on } from '@ember/modifier';
import { t } from 'ember-intl';
import { isPresent } from '@ember/utils';
import SpinnerOverlay from 'eflex/components/spinner-overlay';
import RouteTemplate from 'ember-route-template';
import { isTesting, isDevelopingApp, macroCondition } from '@embroider/macros';
let DataGeneratorTemplate;
if (macroCondition(isDevelopingApp() && !isTesting())) {
    DataGeneratorTemplate = class extends Component {
        @service
        locationRepo;
        @service
        store;
        @service
        areaRepo;
        @service
        groupRepo;
        @service
        stationRepo;
        @service
        taskRepo;
        @tracked
        areaCount = 1;
        @tracked
        modelCount = 10;
        @tracked
        groupCount = 1;
        @tracked
        stationCount = 5;
        @tracked
        taskCount = 10;
        @tracked
        persist = true;
        @tracked
        profile = false;
        @tracked
        results = new TrackedArray();
        @tracked
        totalTimeToCreate;
        @tracked
        totalTimeToSave;
        @tracked
        current;
        get numRecords() {
            let numRecords = this.areaCount;
            numRecords += this.areaCount * this.modelCount;
            numRecords += this.areaCount * this.groupCount;
            numRecords += this.areaCount * this.groupCount * this.stationCount;
            numRecords += this.areaCount * this.groupCount * this.stationCount * this.taskCount;
            return numRecords;
        }
        generate = task(waitFor(async ()=>{
            await timeout(0); // let animation start
            if (this.profile) {
                // eslint-disable-next-line no-console
                console.profile('create');
            }
            const times = {
                areaTimes: [],
                groupTimes: [],
                stationTimes: [],
                taskTimes: [],
                modelTimes: []
            };
            this.current = 1;
            const areas = [];
            const start = performance.now();
            for(let i = 0; i < this.areaCount; i++){
                areas.push(this._createArea(i, times));
            }
            if (this.profile) {
                // eslint-disable-next-line no-console
                console.profileEnd('create');
            }
            Object.assign(this, {
                results: new TrackedArray(),
                totalTimeToCreate: Math.round(performance.now() - start)
            });
            for (const type of [
                'area',
                'model',
                'group',
                'station',
                'task'
            ]){
                const typeTimes = times[`${type}Times`];
                const total = Math.round(sum(typeTimes));
                const average = Math.round(total / typeTimes.length);
                this.results.push({
                    type,
                    times: typeTimes,
                    average,
                    total
                });
            }
            if (this.persist) {
                const allModels = areas.flatMap((area)=>area.models);
                const groups = areas.flatMap((area)=>area.groups);
                const stations = groups.flatMap((group)=>group.stations);
                const tasks = stations.flatMap((station)=>station.tasks);
                const taskConfigs = tasks.flatMap((_task)=>_task.taskConfigs);
                const saveStart = performance.now();
                if (this.profile) {
                    // eslint-disable-next-line no-console
                    console.profile('save');
                }
                await all(areas.map((area)=>area.save()));
                await all(allModels.map((allModel)=>allModel.save()));
                await all(groups.map((group)=>group.save()));
                await this.locationRepo.bulkSave.perform({
                    source: 'data generator',
                    stations,
                    tasks,
                    taskConfigs
                });
                if (this.profile) {
                    // eslint-disable-next-line no-console
                    console.profileEnd('save');
                }
                this.totalTimeToSave = Math.round(performance.now() - saveStart);
            }
        }));
        _createArea(areaId, times) {
            const areaStart = performance.now();
            const area = this.areaRepo.create({
                name: `Area ${areaId}`
            });
            times.areaTimes.push(performance.now() - areaStart);
            this._incrementTotal();
            for(let i = 0; i < this.modelCount; i++){
                const modelStart = performance.now();
                this.store.createRecord('model', {
                    code: i,
                    area
                });
                times.modelTimes.push(performance.now() - modelStart);
                this._incrementTotal();
            }
            for(let g = 0, end1 = this.groupCount; g < end1; g++){
                this._createGroup(g, area, times);
            }
            return area;
        }
        _createGroup(locationNumber, parent, times) {
            const start = performance.now();
            const group = this.groupRepo.create({
                parent,
                name: `Group ${locationNumber}`
            });
            times.groupTimes.push(performance.now() - start);
            this._incrementTotal();
            for(let i = 0; i < this.stationCount; i++){
                this._createStation(i, group, times);
            }
        }
        _createStation(locationNumber, parent, times) {
            const start = performance.now();
            const station = this.stationRepo.create({
                parent,
                name: `Station ${locationNumber}`,
                jem: true
            });
            times.stationTimes.push(performance.now() - start);
            this._incrementTotal();
            for(let i = 0; i < this.taskCount; i++){
                this._createTask(i, station, times);
            }
        }
        _createTask(locationNumber, parent, times) {
            const start = performance.now();
            this.taskRepo.create({
                parent,
                name: `Task ${locationNumber}`,
                taskType: taskTypes.timer
            });
            times.taskTimes.push(performance.now() - start);
            this._incrementTotal();
        }
        _incrementTotal() {
            this.current += 1;
            if (this.current % 500 === 0 || this.current === this.numRecords) {
                // eslint-disable-next-line no-console
                console.log('Created', this.current, 'of', this.numRecords, 'total records.');
            }
        }
        static{
            template_0f757811d496452cbaea16cf4db6c601(`
      <Breadcrumbs @page="Data Generator" />

      <div class="row">
        <div class="col-sm-2">
          <label class="col-form-label">
            Num Areas
          </label>
          <input
            value={{this.areaCount}}
            type="number"
            class="form-control"
            {{on "input" (inputNum (fn (mut this.areaCount)))}}
          />
        </div>
        <div class="col-sm-2">
          <label class="col-form-label">
            Num Models
          </label>
          <input
            value={{this.modelCount}}
            type="number"
            class="form-control"
            {{on "input" (inputNum (fn (mut this.modelCount)))}}
          />
        </div>
        <div class="col-sm-2">
          <label class="col-form-label">
            Num Groups
          </label>
          <input
            value={{this.groupCount}}
            type="number"
            class="form-control"
            {{on "input" (inputNum (fn (mut this.groupCount)))}}
          />
        </div>
        <div class="col-sm-2">
          <label class="col-form-label">
            Num Stations
          </label>
          <input
            value={{this.stationCount}}
            type="number"
            class="form-control"
            {{on "input" (inputNum (fn (mut this.stationCount)))}}
          />
        </div>
        <div class="col-sm-2">
          <label class="col-form-label">
            Num Tasks
          </label>
          <input
            value={{this.taskCount}}
            type="number"
            class="form-control"
            {{on "input" (inputNum (fn (mut this.taskCount)))}}
          />
        </div>
      </div>
      <div class="row">
        <div class="col-sm-2">
          <label class="col-form-label">
            Persist Data?
          </label>
          <CheckboxInput
            @checked={{this.persist}}
            @onChange={{fn (mut this.persist)}}
          />
        </div>
        <div class="col-sm-2">
          <label class="col-form-label">
            Profile?
          </label>
          <CheckboxInput
            @checked={{this.profile}}
            @onChange={{fn (mut this.profile)}}
          />
        </div>
        <div class="col-sm-6"></div>
        <div class="col-sm-2">
          <button
            class="btn btn-secondary btn-sm"
            type="button"
            {{on "click" this.generate.perform}}
          >
            Create Locations
          </button>
        </div>
      </div>

      {{#if this.generate.isRunning}}
        <SpinnerOverlay @message={{t "loading.wait"}} />
      {{else if (isPresent this.results)}}
        <table class="table table-bordered">
          <thead>
            <tr>
              <th>
                Type
              </th>
              <th>
                Number Created
              </th>
              <th>
                Average Time To Create (ms)
              </th>
              <th>
                Total Time (ms)
              </th>
            </tr>
          </thead>
          <tbody>
            {{#each this.results as |result|}}
              <tr>
                <td class="num-col">
                  {{result.type}}
                </td>
                <td class="num-col">
                  {{result.times.length}}
                </td>
                <td class="num-col">
                  {{result.average}}
                </td>
                <td class="num-col">
                  {{result.total}}
                </td>
              </tr>
            {{/each}}
          </tbody>
        </table>
        <h3>
          Total time to create {{this.numRecords}} records: {{this.totalTimeToCreate}}ms
        </h3>
      {{/if}}
      {{#if this.totalTimeToSave}}
        <h3>
          Total time to save: {{this.totalTimeToSave}} ms
        </h3>
      {{/if}}
    `, {
                component: this,
                eval () {
                    return eval(arguments[0]);
                }
            });
        }
    };
} else {
    DataGeneratorTemplate = template_0f757811d496452cbaea16cf4db6c601(``, {
        eval () {
            return eval(arguments[0]);
        }
    });
}
export default RouteTemplate(DataGeneratorTemplate);
